/**
 * Automatic album creation without any server-side AI.
 *
 * The photos are curated in the browser by the PhotoCurator service (duplicates, blurry or badly
 * exposed shots, faces, moments), then laid out moment by moment :
 *  - the cover gets the best photo of the set ; a product with its own cover layouts (product_media
 *    of model ThemeLayout, a collection) keeps that design, an album whose category was recognised
 *    gets the cover drawn for that category (covers.CoverDesigns : a city break in one of its cities
 *    gets the design of that city, a trip in one country the design of that country), a product
 *    without theme the default white design on both sides, see _designCover
 *  - each moment receives a share of the pages proportional to its size, opens on a left page when
 *    it spans a spread, and starts with its best photo alone on a full page
 *  - the other photos of the moment are spread evenly, in chronological order, with the layouts of
 *    LayoutsGenerator fitted to the page (stretched towards the printable area within a crop limit,
 *    see _fitLayout) ; pages are added while the book allows it
 *  - when the photos span several days, every page where the day changes carries the date in two
 *    text lines ("Le jeudi 17" / "Septembre 2026") above the photos, top left ; the layout leaves
 *    the band free
 *  - crops come from smartcrop.js, boosted by the detected faces
 *
 * Kept API : placePhotos(photos) answers a Promise and raises "placing-photos" then
 * "placing-photos-finished" on the book. Should the curation fail, the photos are simply placed in
 * chronological order, one per page, as before.
 */
fx.ns("creators", class ChronologicalCreator {

    constructor(data)
    {
        this.data = data;
        this.photoPlacedAlready = false;
    }

    get book()
    {
        return this.data.book;
    }

    get curator()
    {
        return fx.service("PhotoCurator");
    }

    get generator()
    {
        return fx.service("LayoutsGenerator");
    }

    get settings()
    {
        let settings = fx.get("settings") || {};

        return {
            photosPerPage : settings.curatorPhotosPerPage || 3,
            maxPhotosPerPage : settings.curatorMaxPhotosPerPage || 6,
            heroMinScore : (settings.curatorHeroMinScore !== undefined) ? settings.curatorHeroMinScore : 0.45,
            pageMargin : (settings.curatorPageMargin !== undefined) ? settings.curatorPageMargin : 15,
            coverMargin : (settings.curatorCoverMargin !== undefined) ? settings.curatorCoverMargin : 0.07,        // fraction of the page width
            coverPhotoHeight : (settings.curatorCoverPhotoHeight !== undefined) ? settings.curatorCoverPhotoHeight : 0.75,  // fraction of the page height
            coverTitleFontSize : settings.curatorCoverTitleFontSize || 10,
            coverSubtitleFontSize : settings.curatorCoverSubtitleFontSize || 6,
            coverFont : settings.curatorCoverFont || "Lora",
            coverTitleColor : settings.curatorCoverTitleColor || "rgb(108, 108, 107)",
            coverSubtitleColor : settings.curatorCoverSubtitleColor || "rgb(0, 0, 0)",
            dateFont : settings.curatorDateFont || "Lora",
            dateDayFontSize : settings.curatorDateDayFontSize || 7,      // "Le jeudi 17"
            dateMonthFontSize : settings.curatorDateMonthFontSize || 5,  // "Septembre 2026"
            dateColor : settings.curatorDateColor || "rgb(0, 0, 0)",
            maxCropLoss : (settings.curatorMaxCropLoss !== undefined) ? settings.curatorMaxCropLoss : 0.35,
            coverShapeMinArea : (settings.curatorCoverShapeMinArea !== undefined) ? settings.curatorCoverShapeMinArea : 0.65,  // page fraction a layout shape must cover to be repeated around the photo
            coverShapeMargin : (settings.curatorCoverShapeMargin !== undefined) ? settings.curatorCoverShapeMargin : 10,       // how much bigger than the photo that shape is, in page units
            coverShapePhotoShrink : (settings.curatorCoverShapePhotoShrink !== undefined) ? settings.curatorCoverShapePhotoShrink : 0.1,  // photo reduction on a cover whose collection frames it with a shape
            coverShapeTitleLift : (settings.curatorCoverShapeTitleLift !== undefined) ? settings.curatorCoverShapeTitleLift : 10       // how much the title and its subtitle come up on such a cover, in page units
        };
    }

    /**
     * @param photos   models.Photo[] (defaults to the available photos)
     * @param options  { title : "..." } puts the title on the front cover, { subtitle : "..." } under it
     *                 (else the period of the photos) ; { showDates : false } prevents the day labels,
     *                 otherwise written whenever the photos span several days
     */
    placePhotos(photos, options)
    {
        let list = photos && photos.length ? (photos.toArray ? photos.toArray() : photos.slice()) : this.data.photos.available;
        list = list.filter((photo) => photo && !photo.deleted);

        this._options = options || {};

        // every photo given, before the curation drops duplicates and blurry shots : what the album
        // spans in time and space (_placeCoverLayout)
        this._albumPhotos = list;

        this._flagIaProject();

        this.book.event("placing-photos");

        return this.curator.curate(list, { maxGroups : this._maxGroups(list.length) })
            .then((result) => this._build(result))
            .catch((e) => {
                console.warn("ChronologicalCreator : curation failed, placing photos chronologically", e);
                return this._placeSimple(list);
            })
            .catch((e) => {
                // the simple placement failed too : the album stays as it is, the flow goes on
                console.warn("ChronologicalCreator : simple placement failed", e);
            })
            .then(() => {
                this.curator.release();
                this.photoPlacedAlready = true;
                this.book.event("placing-photos-finished", null, 250);
            });
    }

    /**
     * Statistics : how the album was made (creations.ia_project). 1 is written by the server when
     * the album is created by the magic creation (/create/magic, the `ia` parameter of /app/create)
     * and is kept whatever happens next ; 2 marks an album whose photos were placed automatically
     * from the editor. The property is bound by the server data, so the assignment goes through the
     * saver like any other update of the creation.
     */
    _flagIaProject()
    {
        let creation = this.data.creation;

        if (!creation || !creation.hasOwnProperty("ia_project"))
            return;

        let value = Number(creation.ia_project);

        // a magic album stays a magic album, and the mark is only written once
        if (value == 1 || value == 2)
            return;

        creation.ia_project = 2;
    }

    // ------------------------------- build ------------------------------- //

    _build(result)
    {
        let photos = result.kept;

        if (!photos.length)
            return Promise.resolve();

        this._coverPeriodPhotos = photos;

        return this._chooseCoverPhoto(photos)
            .then((cover) => {
                if (cover)
                    result.cover = cover;

                return this._designCover(this._options.title);
            })
            .then(() => this._buildPages(result, photos));
    }

    /**
     * The cover photo of a category whose design asks for a subject (coverSubject) : a wedding album
     * showing anything but the bride and groom on its cover is a miss, and neither the score nor the
     * faces tell who is who. Claude is asked to pick among the best candidates
     * (TitleSuggester.coverPhoto), and answers null whenever it cannot : the curator's own choice
     * then stands, as for every other category.
     */
    _chooseCoverPhoto(photos)
    {
        let design = this._coverDesign();

        if (!design || !design.coverSubject || photos.length < 2)
            return Promise.resolve(null);

        try
        {
            return fx.service("TitleSuggester").coverPhoto(photos, this._options.category).catch(() => null);
        }
        catch (e)
        {
            return Promise.resolve(null);
        }
    }

    _buildPages(result, photos)
    {
        return this._applyTitle(this._options.title).then(() => this._buildPagesAfterTitle(result, photos));
    }

    _buildPagesAfterTitle(result, photos)
    {
        // dates : one label per page where the day changes as soon as the photos span several days,
        // the year only when several years are covered
        this._showDates = (this._options.showDates !== undefined) ? !!this._options.showDates : this._countDays(photos) > 1;
        this._lastDay = null;

        // the cover photos are not repeated inside the album
        let coverPhotos = this._coverPhotos(result);
        let chain = this._fillCover(coverPhotos);

        // a collection cover without a photo slot : the photo chosen for the cover opens its moment
        // inside the album instead, alone on a page (_chunks)
        this._coverPhotoInside = (!this._coverComponents().length && this._coverKeepTexts) ? this._chosenCoverPhoto(result) : null;

        let groups = result.groups;
        if (coverPhotos.length && coverPhotos.length < photos.length)
        {
            photos = photos.filter((photo) => coverPhotos.indexOf(photo) == -1);
            groups = groups
                .map((group) => Object.assign({}, group, { photos : group.photos.filter((photo) => coverPhotos.indexOf(photo) == -1) }))
                .filter((group) => group.photos.length);
        }

        let plan = this._plan(groups, photos.length);

        this._ensurePages(plan.pages);

        let pages = this.book.innerPages;
        for (let i = 0; i < pages.length; i++)
            pages[i].removeAllContent();

        let sides = this._sides();
        let cursor = 0;

        for (let g = 0; g < plan.groups.length; g++)
        {
            let chunks = plan.groups[g].chunks;

            for (let c = 0; c < chunks.length; c++)
            {
                let slot = sides[cursor++];
                if (!slot)
                    return chain;

                let chunk = chunks[c];
                chain = chain.then(() => this._placeChunk(slot, chunk));
            }
        }

        return chain;
    }

    /**
     * Pages per moment. The book grows from its current size towards photosPerPage photos per page,
     * within its maximum, and every moment gets at least what maxPhotosPerPage requires.
     */
    _plan(groups, total)
    {
        let s = this.settings;
        let current = this._sides().length;
        let max = this.book.maxPages ? Math.max(this.book.maxPages, current) : current;

        let pages = Math.min(max, Math.max(current, Math.ceil(total / s.photosPerPage)));
        if (pages % 2)
            pages = Math.min(max, pages + 1);

        let plans = groups.map((group) => ({
            photos : group.photos.slice(),
            pages : Math.max(1, Math.ceil(group.photos.length / s.maxPhotosPerPage))
        }));

        let minimum = plans.reduce((sum, plan) => sum + plan.pages, 0);
        if (minimum > pages)
        {
            pages = Math.min(max, minimum + (minimum % 2));

            // still too many photos for the book : the biggest moments lose their weakest shots
            if (minimum > pages)
                this._trim(plans, pages * s.maxPhotosPerPage);
        }

        // the remaining pages go, one by one, to the moment with the most photos per page
        let remaining = pages - plans.reduce((sum, plan) => sum + plan.pages, 0);
        while (remaining > 0)
        {
            let best = null, bestLoad = 1;
            for (let i = 0; i < plans.length; i++)
            {
                let load = plans[i].photos.length / plans[i].pages;
                if (load > bestLoad)
                {
                    bestLoad = load;
                    best = plans[i];
                }
            }

            if (!best)
                break;

            best.pages++;
            remaining--;
        }

        // a moment spanning several pages should open on a left page. Either the previous moment
        // takes one more page (when it has photos to fill it), or it gives one up (when it can be
        // denser) and this moment starts one page earlier.
        let minimumPages = (plan) => Math.ceil(plan.photos.length / s.maxPhotosPerPage);

        for (let i = 1; i < plans.length; i++)
        {
            let start = 0;
            for (let j = 0; j < i; j++)
                start += plans[j].pages;

            let startsRight = start % 2 == 0;   // side 0 is page 1 (right), odd sides are left pages
            if (!startsRight || plans[i].pages < 2)
                continue;

            let prev = plans[i - 1], cur = plans[i];

            if (prev.photos.length > prev.pages && cur.pages - 1 >= minimumPages(cur))
            {
                prev.pages++;
                cur.pages--;
            }
            else if (prev.pages - 1 >= minimumPages(prev) && prev.pages >= 2)
            {
                prev.pages--;
                if (cur.photos.length > cur.pages)
                    cur.pages++;
            }
        }

        for (let i = 0; i < plans.length; i++)
            plans[i].chunks = this._chunks(plans[i]);

        return { pages : pages, groups : plans };
    }

    _trim(plans, capacity)
    {
        let count = () => plans.reduce((sum, plan) => sum + plan.photos.length, 0);

        while (count() > capacity)
        {
            let biggest = plans.reduce((a, b) => (b.photos.length > a.photos.length ? b : a));
            if (biggest.photos.length <= 1)
                break;

            let weakest = biggest.photos.reduce((a, b) => (this._score(b) < this._score(a) ? b : a));
            biggest.photos.splice(biggest.photos.indexOf(weakest), 1);
            biggest.pages = Math.max(1, Math.ceil(biggest.photos.length / this.settings.maxPhotosPerPage));
        }
    }

    /**
     * Pages of a moment : the best photo opens it alone when there is room, the rest is spread
     * evenly, chronologically, alternating fuller and lighter pages. The photo chosen for a cover
     * that has no room for it (_coverPhotoInside) opens its moment whatever its score.
     */
    _chunks(plan)
    {
        let photos = plan.photos.slice();
        let pages = plan.pages;
        let chunks = [];

        // the opening page is only taken when the other pages can still hold the rest of the moment
        if (pages >= 2 && photos.length >= 3 && photos.length - 1 <= (pages - 1) * this.settings.maxPhotosPerPage)
        {
            let chosen = (this._coverPhotoInside && photos.indexOf(this._coverPhotoInside) != -1) ? this._coverPhotoInside : null;
            let best = chosen || photos.reduce((a, b) => (this._score(b) > this._score(a) ? b : a));

            if (chosen || this._score(best) >= this.settings.heroMinScore)
            {
                photos.splice(photos.indexOf(best), 1);
                chunks.push({ photos : [best], hero : true });
                pages--;
            }
        }

        let sizes = this._balancedSizes(photos.length, pages);
        for (let i = 0; i < sizes.length; i++)
            if (sizes[i] > 0)
                chunks.push({ photos : photos.splice(0, sizes[i]), hero : false });

        return chunks;
    }

    _balancedSizes(count, pages)
    {
        if (count <= 0)
            return [];

        pages = Math.max(1, Math.min(pages, count));

        let base = Math.floor(count / pages), extra = count % pages;
        let sizes = [];
        for (let i = 0; i < pages; i++)
            sizes.push(base + (i < extra ? 1 : 0));

        if (extra > 0 && pages > 2)
        {
            let big = sizes.filter((size) => size == base + 1), small = sizes.filter((size) => size == base);
            sizes = [];
            while (big.length || small.length)
            {
                if (big.length) sizes.push(big.pop());
                if (small.length) sizes.push(small.pop());
            }
        }

        return sizes;
    }

    // ------------------------------- pages ------------------------------- //

    /** Printable sides in reading order : { page, side }. Page 1 is the right side of the first sheet. */
    _sides()
    {
        let pages = this.book.innerPages, sides = [];

        for (let i = 0; i < pages.length; i++)
        {
            if (i > 0)
                sides.push({ page : pages[i], side : "left" });

            if (i < pages.length - 1)
                sides.push({ page : pages[i], side : "right" });
        }

        return sides;
    }

    _ensurePages(count)
    {
        let guard = 0;

        while (this._sides().length < count && this.book.canAdd && guard++ < 500)
        {
            let pages = this.book.innerPages;
            let page = this.book.addPage(pages[pages.length - 1], "right", true, true, this.data.layouts);
            page.removeAllContent();
        }
    }

    _placeChunk(slot, chunk)
    {
        let photos = chunk.photos;

        if (!photos.length)
            return Promise.resolve();

        let width = this.book.pageWidth, height = this.book.pageHeight;
        let single = photos.length == 1 && (chunk.hero || this._score(photos[0]) >= this.settings.heroMinScore);

        let date = this._showDates ? this._dateFor(photos) : null;
        let variant = date ? "top" : null;   // the date above the photos, top left
        let reserve = date ? this._dateReserve(height) : 0;

        let candidates = single ? [this._fullPageLayout(width, height)] : this.generator.getLayouts(photos);
        let layout = this._bestLayout(candidates, photos, width, height, reserve, variant);
        if (!layout)
            return Promise.resolve();

        slot.page.applyLayout(layout, width, height, 0, slot.side);

        if (date)
            this._addDateTexts(slot.page, slot.side, date, variant, width, height);

        let components = this._photoComponents(slot.page, slot.side);
        let chain = Promise.resolve();

        for (let i = 0; i < components.length && i < photos.length; i++)
        {
            let component = components[i], photo = photos[i];
            chain = chain.then(() => this._assign(component, photo, single));
        }

        return chain;
    }

    // ------------------------------- dates ------------------------------- //

    /**
     * Date of the page when one of its photos opens a new day (the first new day), null otherwise.
     * The last day of the page becomes the reference for the next one.
     */
    _dateFor(photos)
    {
        let date = null;

        for (let i = 0; i < photos.length; i++)
        {
            let day = this._dayOf(photos[i]);
            if (!day)
                continue;

            if (day != this._lastDay && !date)
                date = new Date(curator.MomentClustering.time(photos[i]) * 1000);

            this._lastDay = day;
        }

        return date;
    }

    _dayOf(photo)
    {
        let time = curator.MomentClustering.time(photo);
        if (!time)
            return null;

        let date = new Date(time * 1000);
        return date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate();
    }

    _countDays(photos)
    {
        let days = {};
        for (let i = 0; i < photos.length; i++)
        {
            let day = this._dayOf(photos[i]);
            if (day)
                days[day] = true;
        }
        return Object.keys(days).length;
    }

    _countYears(photos)
    {
        let years = {};
        for (let i = 0; i < photos.length; i++)
        {
            let time = curator.MomentClustering.time(photos[i]);
            if (time)
                years[new Date(time * 1000).getFullYear()] = true;
        }
        return Object.keys(years).length;
    }

    get _locale()
    {
        let locales = { fr : "fr-FR", en : "en-GB", es : "es-ES", de : "de-DE", it : "it-IT" };
        let lang = (typeof fx.currentLang == "function") ? fx.currentLang() : "fr";
        return locales[lang] || "fr-FR";
    }

    /** "Le jeudi 17" */
    _dayLine(date)
    {
        return fx.text("Le $", date.toLocaleDateString(this._locale, { weekday : "long", day : "numeric" }));
    }

    /** "Septembre 2026" */
    _monthLine(date)
    {
        let text = date.toLocaleDateString(this._locale, { month : "long", year : "numeric" });
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    /** Height kept free for the two date lines, above or under the photos. */
    _dateReserve(height)
    {
        let s = this.settings;
        return Math.min(height / 4, s.dateDayFontSize * 2 + s.dateMonthFontSize * 2 + 4 + height * 0.05);
    }

    /**
     * The two date lines of a page. "bottom" : right aligned under the photos ; "top" : left aligned
     * above them. Both in the date font, black, day line bigger than the month line.
     */
    _addDateTexts(page, side, date, variant, width, height)
    {
        let s = this.settings;
        let margin = Math.max(0, Math.min(s.pageMargin, width / 4, height / 4));
        let reserve = this._dateReserve(height);
        let offset = (side == "right") ? width : 0;
        let blockWidth = width * 0.6;
        let dayHeight = s.dateDayFontSize * 2;

        let x = (variant == "top") ? offset + margin : offset + width - margin - blockWidth;
        let align = (variant == "top") ? "left" : "right";
        let y = (variant == "top") ? margin + reserve * 0.15 : height - margin - reserve + reserve * 0.2;

        this._addStyledText(page, side, this._dayLine(date), { x : x, y : y, width : blockWidth }, s.dateDayFontSize, s.dateColor, align, width, height);
        this._addStyledText(page, side, this._monthLine(date), { x : x, y : y + dayHeight, width : blockWidth }, s.dateMonthFontSize, s.dateColor, align, width, height);
    }

    _addStyledText(page, side, text, rect, fontSize, color, align, width, height)
    {
        let s = this.settings;
        let safe = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
        let font = (this.data.fonts && this.data.fonts.find) ? this.data.fonts.find({ name : s.dateFont }) : null;

        page.addComponent({
            component_type : "text",
            content_type : "text",
            content : "<p style=\"text-align: " + align + ";\"><span style=\"font-family: " + s.dateFont + "; font-size: " + fontSize + "px; color: " + color + ";\">" + safe + "</span></p>",
            text_color : color,
            font_family : font ? font.id : 1,
            font_size : fontSize,
            align : align,
            x : rect.x,
            y : rect.y,
            width : rect.width,
            height : fontSize * 3,
            height_inner : -1   // the text component resizes itself to its content
        }, side, width, height, 0, false);
    }

    /**
     * Among the generator's candidates (cells shaped like the photos, so they rarely fill the page),
     * keeps the one that covers the printable area best once fitted, crops included.
     */
    _bestLayout(candidates, photos, width, height, reserve, reserveAt)
    {
        let best = null;

        for (let i = 0; i < candidates.length && i < 30; i++)
        {
            let fit = this._fitLayout(candidates[i], photos, width, height, reserve, reserveAt);
            if (!fit)
                continue;

            let score = fit.coverage - 0.5 * fit.loss;
            if (!best || score > best.score)
                best = { fit : fit, score : score };
        }

        return best ? best.fit.layout : (candidates[0] || null);
    }

    /**
     * Fits a layout to the page : its bounding box is stretched towards the printable area (page
     * minus pageMargin) as far as the crops this implies stay under maxCropLoss for every photo,
     * then centered, the leftover white being shared around. Cells keep their relative positions,
     * their ratios change and smartcrop absorbs the difference.
     *
     * Answers { layout, loss (worst crop ratio 0..1), coverage (0..1 of the printable area) }.
     */
    _fitLayout(layout, photos, width, height, reserve, reserveAt)
    {
        let s = this.settings;
        let margin = Math.max(0, Math.min(s.pageMargin, width / 4, height / 4));
        reserve = Math.max(0, Math.min(reserve || 0, height / 3));   // band kept free for the date, at the top or the bottom
        let top = (reserveAt == "bottom") ? 0 : reserve;
        let inner = { x : margin, y : margin + top, width : width - 2 * margin, height : height - 2 * margin - reserve };

        let cells = (layout.components.toArray ? layout.components.toArray() : layout.components).filter((c) => c.component_type == "photo");
        if (!cells.length)
            return null;

        let box = { x : Infinity, y : Infinity, right : -Infinity, bottom : -Infinity };
        for (let i = 0; i < cells.length; i++)
        {
            let c = cells[i];
            box.x = Math.min(box.x, Number(c.x));
            box.y = Math.min(box.y, Number(c.y));
            box.right = Math.max(box.right, Number(c.x) + Number(c.width));
            box.bottom = Math.max(box.bottom, Number(c.y) + Number(c.height));
        }
        box.width = box.right - box.x;
        box.height = box.bottom - box.y;
        if (!(box.width > 0) || !(box.height > 0))
            return null;

        let sx = inner.width / box.width, sy = inner.height / box.height;

        // photos land in the cells in reading order (see _photoComponents)
        let order = cells.slice().sort((a, b) => {
            let ay = Number(a.y) + Number(a.height) / 2, by = Number(b.y) + Number(b.height) / 2;
            let ax = Number(a.x) + Number(a.width) / 2, bx = Number(b.x) + Number(b.width) / 2;
            return (Math.abs(ay - by) > 1) ? ay - by : ax - bx;
        });
        let ratios = order.map((cell, i) => (photos[i] && photos[i].width && photos[i].height) ? photos[i].width / photos[i].height : 0);

        // the crops only depend on q = scaleX / scaleY : worst crop over the cells for a given q
        let lossOf = (q) => {
            let worst = 0;
            for (let i = 0; i < order.length; i++)
            {
                if (!ratios[i])
                    continue;

                let r = q * Number(order[i].width) / Number(order[i].height);
                worst = Math.max(worst, 1 - Math.min(r / ratios[i], ratios[i] / r));
            }
            return worst;
        };

        // q* fills the printable area ; when its crops are too deep, move q towards the value that
        // crops the least until the limit is met
        let fill = sx / sy;
        let q = fill;

        if (lossOf(fill) > s.maxCropLoss)
        {
            let ideal = fill, idealLoss = lossOf(fill);
            for (let i = 0; i < order.length; i++)
            {
                if (!ratios[i])
                    continue;

                let candidate = ratios[i] * Number(order[i].height) / Number(order[i].width);
                let candidateLoss = lossOf(candidate);
                if (candidateLoss < idealLoss)
                {
                    ideal = candidate;
                    idealLoss = candidateLoss;
                }
            }

            if (idealLoss <= s.maxCropLoss)
            {
                // largest step from ideal towards fill (in log space) that keeps the crops acceptable
                let lo = 0, hi = 1;
                for (let i = 0; i < 16; i++)
                {
                    let mid = (lo + hi) / 2;
                    if (lossOf(ideal * Math.pow(fill / ideal, mid)) <= s.maxCropLoss)
                        lo = mid;
                    else
                        hi = mid;
                }
                q = ideal * Math.pow(fill / ideal, lo);
            }
            else
                q = ideal;
        }

        // largest scales with that ratio inside the printable area
        let scaleX = Math.min(sx, q * sy), scaleY = scaleX / q;

        let fittedWidth = box.width * scaleX, fittedHeight = box.height * scaleY;
        let offsetX = inner.x + (inner.width - fittedWidth) / 2;
        let offsetY = inner.y + (inner.height - fittedHeight) / 2;

        let components = cells.map((c) => new models.ThemeLayoutComponent({
            component_type : "photo",
            x : offsetX + (Number(c.x) - box.x) * scaleX,
            y : offsetY + (Number(c.y) - box.y) * scaleY,
            width : Number(c.width) * scaleX,
            height : Number(c.height) * scaleY,
            width_inner : 0,
            height_inner : 0
        }));

        return {
            layout : new models.ThemeLayout({ components : components }),
            loss : lossOf(q),
            coverage : (fittedWidth * fittedHeight) / (inner.width * inner.height)
        };
    }

    _fullPageLayout(width, height)
    {
        let component = new models.ThemeLayoutComponent({
            component_type : "photo",
            x : 0,
            y : 0,
            width : width,
            height : height,
            width_inner : 0,
            height_inner : 0
        });

        return new models.ThemeLayout({ components : [component] });
    }

    /** Photo components of one side, in reading order (top to bottom, then left to right). */
    _photoComponents(page, side)
    {
        let components = page.getComponents(side, this.book.pageWidth, 0).filter((component) => component.component_type == "photo");

        return components.sort((a, b) => {
            let ay = a.y + a.height / 2, by = b.y + b.height / 2;
            let ax = a.x + a.width / 2, bx = b.x + b.width / 2;
            return (Math.abs(ay - by) > 1) ? ay - by : ax - bx;
        });
    }

    // ------------------------------- cover ------------------------------- //

    /**
     * Chooses the cover design. A product carrying its own cover layouts (product_media of model
     * ThemeLayout, target "cover" or typeid 6 : the customer picked an album from a collection) keeps
     * them, and nothing is added to them :
     *  - one of those layouts names where the photos were taken (a place keyword equal to their city,
     *    region or country, _placeCoverLayout) : it is applied, whatever the rules below
     *  - the current cover already shows a photo (and is not one of our earlier designs) : kept, the
     *    photo filled in (_fillCover)
     *  - otherwise the first of those layouts with a photo is applied, the curated photo going in it
     *  - none has a photo : the first one is applied as it is, with no photo
     * The title and the subtitle then go into the texts the cover has, when it has some (_applyTitle),
     * never into a text of our own.
     * A product without collection layouts whose album category was recognised (options.category,
     * the TitleSuggester classification) gets the cover drawn for that category instead, see
     * covers.CoverDesigns.
     * Answers a Promise, the medias of a collection layout being loaded from the server.
     */
    _designCover(title)
    {
        this._coverTitleRect = null;
        this._coverShapeSource = null;
        this._coverTextUpper = false;
        this._coverTextColor = null;
        this._coverPhotoScale = 1;
        this._designedCover = null;

        this._coverKeepTexts = false;

        let layouts = this._collectionCoverLayouts();

        if (layouts.length)
        {
            // the look the collection gives its cover : the colour and the case of its title, and
            // its large shape. Always read from the layouts, never from the cover, which may carry
            // the result of an earlier automatic creation rather than the design of the product.
            this._readCollectionStyle(layouts);

            // a collection design is never added to : its title and subtitle go into the texts it
            // has, when it has some (_applyTitle)
            this._coverKeepTexts = true;

            // the cover naming where the photos were taken comes before anything else
            return this._placeCoverLayout(layouts).then((placed) => this._applyCollectionCover(layouts, placed));
        }

        // the album category was recognised : its own cover (a city break, a country, the
        // category, see covers.CoverDesigns), the photo placed and masked the way that design wants
        // it, the texts written by DesignedCover.writeTitle
        let design = this._coverDesign();

        if (design)
        {
            this._designedCover = new covers.DesignedCover(this, design);

            return this._designedCover.layout();
        }

        // a product without theme (use_media 0, or no product) : the default cover, white on both
        // sides, whatever theme the album carries for its page layouts
        if (!this.book.useMedias)
        {
            this._plainBackCover();
            this._titleCoverLayout(title, { force : true });

            return Promise.resolve();
        }

        this._prepareCover();
        this._titleCoverLayout(title);

        return Promise.resolve();
    }

    /**
     * Applies the collection cover : `placed`, the one naming where the photos were taken, when
     * there is one (_placeCoverLayout), then the rules of _designCover.
     */
    _applyCollectionCover(layouts, placed)
    {
        if (placed)
            return this._applyCollectionLayout(placed);

        // the cover already holds a design of the collection with a photo : it is kept, with the
        // shape of the collection around the photo when the cover has none. A cover left by one
        // of our own earlier creations is built again instead, so that running the creation
        // twice on the same album gives the same result.
        if (this._coverHasPhoto() && !this._coverIsOurDesign())
        {
            this._frameCoverPhoto();
            this._shapesUnderPhoto();

            return Promise.resolve();
        }

        // the first of the covers with a photo : the curated photo goes in it
        // none : the first cover as it is, no photo added
        return this._applyCollectionLayout(layouts.find((candidate) => this._layoutPhoto(candidate)) || layouts[0]);
    }

    /** One cover of the collection on the front cover : with the curated photo when it has a slot, as it is otherwise. */
    _applyCollectionLayout(layout)
    {
        let book = this.book;
        let withPhoto = !!this._layoutPhoto(layout);

        // the layout brings its own texts : each one keeps the style it was written in
        this._coverTextColor = null;
        this._coverTextUpper = false;

        return this._loadLayoutMedias(layout).then(() => {
            book.cover.applyLayout(withPhoto ? this._framedLayout(layout) : layout, book.pageWidth, book.pageHeight, book.sliceWidth, "right");

            if (withPhoto)
                this._shapesUnderPhoto();
        });
    }

    /**
     * The collection cover naming where the photos were taken : a place keyword of the cover equal
     * to the city, the region or the country of their positions (LayoutsController action
     * placecovers, the covers geocoded and tagged in English). The most precise place wins (a
     * "London" cover before a "United Kingdom" one), and among the covers of that place the one with
     * a photo slot. Answers a Promise of the layout, null when none matches, the photos carry no
     * position, or the server does not answer within PLACE_COVER_TIMEOUT.
     */
    _placeCoverLayout(layouts)
    {
        let points = [];

        try
        {
            // the dates and positions of the whole album, not of the photos the curation kept
            let suggester = fx.service("TitleSuggester");
            points = suggester.coverPoints(suggester.context(this._albumPhotos || this._coverPeriodPhotos || []));
        }
        catch (e) {}

        if (!points.length || !this.data.layouts || !this.data.layouts.matchPlaceCovers)
            return Promise.resolve(null);

        return this.data.layouts.matchPlaceCovers(layouts.map((layout) => layout.id), points, this.PLACE_COVER_TIMEOUT).then((matches) => {
            if (!matches.length)
                return null;

            // the covers of the most precise place found, the one with a photo slot first
            let best = matches.filter((match) => match.keyword == matches[0].keyword)
                .map((match) => layouts.find((layout) => layout.id == match.id))
                .filter((layout) => !!layout);

            return best.find((layout) => this._layoutPhoto(layout)) || best[0] || null;
        });
    }

    get PLACE_COVER_TIMEOUT()
    {
        return 10000;
    }

    /**
     * The design of covers.CoverDesigns the album gets, null when none : chosen from the category
     * the album was recognised as and from every photo given (where and when they were taken).
     */
    _coverDesign()
    {
        return covers.CoverDesigns.choose(this._options || {}, this._albumPhotos || this._coverPeriodPhotos || []);
    }

    /** Back cover and slice of the default design : nothing but a plain background, white by default. */
    _plainBackCover(color)
    {
        let cover = this.book.cover, width = this.book.pageWidth, slice = this.book.sliceWidth || 0;
        color = color || "#ffffff";

        let components = cover.getComponents("left", width, slice).filter((c) => c.component_type != "background");
        for (let i = 0; i < components.length; i++)
            components[i].delete();

        let back = cover.getBackground ? cover.getBackground("left") : null;
        if (back)
            back.update({ content : "", bgcolor : color, align : "" });

        let center = cover.getBackground ? cover.getBackground("slice") : null;
        if (center)
            center.update({ content : "", bgcolor : color, align : "" });

        // the three cover backgrounds share one align : a front left "split" keeps its place past
        // the slice while the slice background, no longer split, is never sized, and the slice
        // can no longer be clicked to write on it
        let front = cover.getBackground ? cover.getBackground("right") : null;
        if (front)
            front.update({ align : "" });
    }

    /**
     * 0 (black) .. 1 (white) of a background component : its picture averaged on a small canvas,
     * else its colour ; 1 when nothing can be read (a white page).
     */
    _backgroundLuminance(background)
    {
        if (!background)
            return Promise.resolve(1);

        let fromColor = () => this._colorLuminance(background.bgcolor);

        let media = (background.content && this.data.backgrounds && this.data.backgrounds.find) ? this.data.backgrounds.find({ id : background.content }) : null;
        let url = media ? (media.thumb || media.light_url || media.url) : null;

        if (!url || typeof Image == "undefined")
            return Promise.resolve(fromColor());

        return new Promise((resolve) => {
            let image = new Image();
            let timer = setTimeout(() => resolve(fromColor()), 6000);

            image.crossOrigin = "anonymous";
            image.onload = () => {
                clearTimeout(timer);
                try
                {
                    let size = 16;
                    let canvas = document.createElement("canvas");
                    canvas.width = size;
                    canvas.height = size;
                    let context = canvas.getContext("2d");
                    context.drawImage(image, 0, 0, size, size);

                    let data = context.getImageData(0, 0, size, size).data;
                    let sum = 0, count = 0;
                    for (let i = 0; i < data.length; i += 4)
                    {
                        let alpha = data[i + 3] / 255;
                        // transparent pixels show the page colour behind
                        let r = data[i] * alpha + 255 * (1 - alpha), g = data[i + 1] * alpha + 255 * (1 - alpha), b = data[i + 2] * alpha + 255 * (1 - alpha);
                        sum += (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
                        count++;
                    }
                    resolve(count ? sum / count : fromColor());
                }
                catch (e)
                {
                    resolve(fromColor());   // tainted canvas
                }
            };
            image.onerror = () => { clearTimeout(timer); resolve(fromColor()); };
            image.src = url;
        });
    }

    /**
     * Luminance of a css colour (anything tinycolor reads : hex, rgb(), rgba(), hsl(), a name...),
     * a transparent colour showing the white page through ; 1 when unreadable or transparent.
     */
    _colorLuminance(color)
    {
        let parsed = (typeof tinycolor == "function") ? tinycolor(String(color || "").trim()) : null;

        if (!parsed || !parsed.isValid())
            return 1;

        let rgb = parsed.toRgb();
        let alpha = parsed.getAlpha();
        let luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;

        return luminance * alpha + 1 * (1 - alpha);
    }

    /** Cover layouts of the product of the creation, default first, none for a product without any. */
    _collectionCoverLayouts()
    {
        let product = this.data.creation ? this.data.creation.product : null;
        let layouts = (product && product.layouts) ? product.layouts : null;

        if (!layouts || !layouts.length)
            return [];

        return (layouts.toArray ? layouts.toArray() : layouts.slice())
            .filter((layout) => layout && layout.components && (!layout.target || layout.target == "cover" || layout.typeid == 6))
            .sort((a, b) => (b.isdefault ? 1 : 0) - (a.isdefault ? 1 : 0));
    }

    /**
     * A layout carrying a shape over coverShapeMinArea of the page (a frame, a panel : the design of
     * the collection) gets that shape repeated around its cover photo : a similar component (kind,
     * border and its colour, fill), coverShapeMargin bigger than the photo and centered on it, right
     * before it in the layout so the picture is laid over it.
     *
     * The copy is made on the layout rather than on the cover : the stage draws the components in the
     * order they are added, so one added afterwards would cover the photo whatever its level.
     */
    _framedLayout(layout)
    {
        let source = this._largeShape(layout);

        if (!source)
            return layout;

        let json = layout.JSON();
        let components = (json.components || []).slice();
        let photo = components.find((component) => component.component_type == "photo");

        if (!photo || !(Number(photo.width) > 0) || !(Number(photo.height) > 0))
            return layout;

        let margin = this.settings.coverShapeMargin;

        components.splice(components.indexOf(photo), 0, Object.assign({}, source, {
            component_type : "shape",
            content : source.content,        // the shape itself : without it the stage has nothing to draw
            borderid : source.borderid,
            side : source.side,
            x : Number(photo.x) - margin / 2,
            y : Number(photo.y) - margin / 2,
            width : Number(photo.width) + margin,
            height : Number(photo.height) + margin,
            rotation : Number(photo.rotation) || 0
        }));

        json.components = components;

        return new models.ThemeLayout(json);
    }

    /**
     * The look of the collection : the large shape of its first cover layout, and the colour and the
     * case of the title. The title is looked for in the following layouts as well when the first one
     * carries no text at all, the layouts of a collection sharing the same palette ; the shape is
     * only taken from the first one, as it shapes the whole cover.
     */
    _readCollectionStyle(layouts)
    {
        this._coverShapeSource = this._largeShape(layouts[0]);

        for (let i = 0; i < layouts.length && !this._coverTextColor; i++)
        {
            let style = this._textStyle(this._layoutTitle(layouts[i]));

            if (style && style.color)
            {
                this._coverTextColor = style.color;
                this._coverTextUpper = style.upper;
            }
        }
    }

    /**
     * The shape of the collection around the photo of a cover we keep as it is. Added on top of
     * everything, it is sent back under the photo the way the editor sends a component down.
     */
    _frameCoverPhoto()
    {
        let photo = this._coverComponents()[0];

        if (!this._coverShapeSource || !photo || !(Number(photo.width) > 0) || !(Number(photo.height) > 0))
            return;

        let margin = this.settings.coverShapeMargin;
        let cover = this.book.cover;
        let components = cover.getComponents("right", this.book.pageWidth, this.book.sliceWidth || 0);

        // a shape already framing the photo (the design of the collection, or ours from an earlier
        // creation) : nothing to add
        let framed = components.some((component) => component.component_type == "shape"
            && Math.abs(Number(component.width) - Number(photo.width)) <= 2 * margin
            && Math.abs(Number(component.height) - Number(photo.height)) <= 2 * margin);

        if (framed)
            return;

        cover.addComponent(Object.assign({}, this._coverShapeSource.JSON ? this._coverShapeSource.JSON() : this._coverShapeSource, {
            component_type : "shape",
            x : Number(photo.x) - margin / 2,
            y : Number(photo.y) - margin / 2,
            width : Number(photo.width) + margin,
            height : Number(photo.height) + margin,
            rotation : 0,
            creation : photo.creation
        }), "right", this.book.pageWidth, this.book.pageHeight, this.book.sliceWidth || 0, true);

        let added = cover.components.getItemAt(cover.components.length - 1);

        if (added)
            added.update("update-level-down");
    }

    /** The photo component of a layout, when it has one. */
    _layoutPhoto(layout)
    {
        let components = (layout && layout.components) ? (layout.components.toArray ? layout.components.toArray() : layout.components) : [];

        return components.find((component) => component.component_type == "photo") || null;
    }

    /** The text of a layout that stands for its title : the biggest one. */
    _layoutTitle(layout)
    {
        let components = (layout && layout.components) ? (layout.components.toArray ? layout.components.toArray() : layout.components) : [];

        return components
            .filter((component) => component.component_type == "text")
            .sort((a, b) => Number(b.width) * Number(b.height) - Number(a.width) * Number(a.height))[0] || null;
    }

    /**
     * How a layout text is written : its colour (the one of its own run, the component's otherwise,
     * as a component often keeps a black text_color under a coloured run) and whether it is in capitals.
     */
    _textStyle(component)
    {
        if (!component)
            return null;

        let doc = (typeof DOMParser != "undefined") ? new DOMParser().parseFromString(String(component.content || ""), "text/html") : null;
        let run = doc ? doc.body.querySelector("[style*='color']") : null;

        return {
            color : (run && run.style.color) ? run.style.color : (component.text_color || ""),
            upper : this._allCaps(doc ? doc.body.textContent : "")
        };
    }

    /**
     * The large shapes of the cover belong under the photo : a frame or a panel of the collection
     * drawn over the picture hides it. They are sent to the back the way the editor does it, the
     * tag the stage listens to in order to sort its components again.
     */
    _shapesUnderPhoto()
    {
        let photo = this._coverComponents()[0];

        if (!photo)
            return;

        let area = this.book.pageWidth * this.book.pageHeight;
        let components = this.book.cover.getComponents("right", this.book.pageWidth, this.book.sliceWidth || 0);

        components
            .filter((component) => component.component_type == "shape"
                && Number(component.width) * Number(component.height) > area * this.settings.coverShapeMinArea
                && Number(component.level) > Number(photo.level))
            .forEach((shape) => shape.update("update-level-down"));
    }

    /** The biggest shape of a layout, when it covers more than coverShapeMinArea of the page. */
    _largeShape(layout)
    {
        if (!layout || !layout.components)
            return null;

        let area = this.book.pageWidth * this.book.pageHeight;
        if (!(area > 0))
            return null;

        let components = layout.components.toArray ? layout.components.toArray() : layout.components;
        let shapes = components.filter((component) => component.component_type == "shape" && Number(component.width) * Number(component.height) > area * this.settings.coverShapeMinArea);

        return shapes.sort((a, b) => Number(b.width) * Number(b.height) - Number(a.width) * Number(a.height))[0] || null;
    }

    /**
     * The title and the subtitle into the texts of a kept design (collection cover, theme cover),
     * their styles kept. The title goes into the title text (texts[0]) when it holds there without
     * shrinking below TITLE_MIN_SCALE. Too long for it on a collection cover, it leaves that text as
     * it is (often the design's own heading, "Livre d'or") and goes, first, into the text printed
     * with the sample date, else into the next text, as long as it stays legible there
     * (TITLE_MIN_FONT once fitted) ; neither is : it stays in the title text, reduced. The subtitle
     * takes the place of the sample date ("Avril 2025"), else of another text left, never of the
     * title text ; there is no subtitle when no text is left.
     */
    _applyTitleToTexts(title, texts)
    {
        let first = texts[0];
        let fitIn = (block) => this._fitOneLineMeasured(this._replaceText(block.content, title, this._replacedTextStyle(block)), block.width);
        let legible = (block, fit) => this._largestFontSize(block.content) * fit.ratio >= this.TITLE_MIN_FONT;

        return fitIn(first)
            .then((fit) => {
                if (!this._coverKeepTexts || texts.length < 2 || fit.ratio >= this.TITLE_MIN_SCALE)
                    return { block : first, fit : fit };

                // the sample date first, then the other texts in their order
                let rest = texts.slice(1);
                let date = this._sampleDateText(rest);
                let candidates = date ? [date].concat(rest.filter((text) => text != date)) : rest;

                let tryAt = (i) => {
                    if (i >= candidates.length)
                        return { block : first, fit : fit };

                    return fitIn(candidates[i]).then((other) => legible(candidates[i], other) ? { block : candidates[i], fit : other } : tryAt(i + 1));
                };

                return tryAt(0);
            })
            .then((chosen) => {
                let others = texts.filter((text) => text != first && text != chosen.block);
                let target = this._sampleDateText(others) || others[0] || null;
                let period = this._subtitleFor(title);

                chosen.block.update("update-content", { content : chosen.fit.html });

                if (target && period)
                    return this._fitOneLine(this._replaceText(target.content, period, this._replacedTextStyle(target)), target.width)
                        .then((content) => target.update("update-content", { content : content }));

                if (target && this._subtitle())
                    target.delete();   // it would only say the title again
            });
    }

    /** Below this scale of its font, a title is too long for the text of a collection cover (see _applyTitleToTexts). */
    get TITLE_MIN_SCALE()
    {
        return 0.75;
    }

    /**
     * The smallest font a title moved out of the title text may end up with, in page units
     * (millimetres) : 3.5 mm, about 10 pt once printed.
     */
    get TITLE_MIN_FONT()
    {
        return 3.5;
    }

    /** The largest font size (px) of a text's HTML content, 0 when it carries none. */
    _largestFontSize(html)
    {
        let sizes = String(html || "").match(/font-size:\s*([\d.]+)px/g) || [];

        return sizes.reduce((max, size) => Math.max(max, parseFloat(size.replace(/[^\d.]/g, "")) || 0), 0);
    }


    /**
     * Among cover texts, the one printed with a sample date : a text that is nothing but a date
     * ("2025", "Avril 2025", "12 juin 2025", "du 10 au 13 avril 2026"), else one holding a year
     * ("Été 2025"). A name that merely starts like a month ("Martin", "Mariage") is not one. Null when none.
     */
    _sampleDateText(texts)
    {
        let plain = (text) => String(text.content || "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

        return texts.find((text) => this.SAMPLE_DATE.test(plain(text)))
            || texts.find((text) => /(^|\D)(19|20)\d{2}(\D|$)/.test(plain(text)))
            || null;
    }

    /** A text made of a date only, in the five languages of the site : see _sampleDateText. */
    get SAMPLE_DATE()
    {
        if (!this._sampleDate)
        {
            let month = "janvier|janv|février|fevrier|févr|fevr|mars|avril|avr|mai|juin|juillet|juil|août|aout|septembre|sept|octobre|oct|novembre|nov|décembre|decembre|déc|dec"
                + "|january|jan|february|feb|march|mar|april|apr|may|june|jun|july|jul|august|aug|september|sep|october|november|december"
                + "|enero|ene|febrero|marzo|abril|abr|mayo|junio|julio|agosto|ago|septiembre|setiembre|octubre|noviembre|diciembre|dic"
                + "|januar|jänner|februar|märz|maerz|juni|juli|oktober|okt|dezember|dez"
                + "|gennaio|gen|febbraio|aprile|maggio|mag|giugno|giu|luglio|lug|settembre|set|ottobre|ott|dicembre";
            let day = "\\d{1,2}(er|st|nd|rd|th|°)?";

            this._sampleDate = new RegExp("^((du|from|del|vom|dal)\\s+" + day + "\\s+(au|to|al|bis)\\s+)?(" + day + "\\s+)?"
                + "(" + month + ")\\.?(\\s+\\d{4})?$|^\\d{4}$", "i");
        }

        return this._sampleDate;
    }

    /** Front cover with at least a photo. */
    _coverHasPhoto()
    {
        let components = this.book.cover.getComponents("right", this.book.pageWidth, this.book.sliceWidth || 0);

        return components.some((c) => c.component_type == "photo");
    }

    /** Texts of the front cover, the "title" one first, then the biggest first. */
    _coverTexts()
    {
        let cover = this.book.cover, width = this.book.pageWidth, slice = this.book.sliceWidth || 0;
        let texts = cover.getComponents("right", width, slice).filter((c) => c.component_type == "text" && c.content_type != "slice-text");

        texts.sort((a, b) => b.width * b.height - a.width * a.height);

        let title = texts.find((c) => c.content_type == "title");
        if (title)
            texts.splice(texts.indexOf(title), 1), texts.unshift(title);

        return texts;
    }

    /** Stickers and backgrounds of a layout the album does not hold yet, fetched before it is applied. */
    _loadLayoutMedias(layout)
    {
        let medias = [];
        let components = layout.components.toArray ? layout.components.toArray() : layout.components;

        for (let i = 0; i < components.length; i++)
        {
            let component = components[i];
            let type = component.isSticker ? "sticker" : (component.isBackground ? "background" : "");
            if (!type || !component.content)
                continue;

            let repository = (type == "sticker") ? this.data.stickers : this.data.backgrounds;
            if (repository && repository.find && repository.find({ id : component.content }))
                continue;

            if (!medias.some((media) => media.type == type && media.id == component.content))
                medias.push({ type : type, id : component.content });
        }

        if (!medias.length || typeof this.data.loadLayoutMedias != "function")
            return Promise.resolve();

        return new Promise((resolve) => {
            let dispatcher = new fx.EventDispatcher();
            let timer = null;

            let done = () => {
                clearTimeout(timer);
                dispatcher.off("event", handler, this.data);
                resolve();
            };

            let handler = (e) => {
                let event = e && e.detail ? e.detail.event : null;
                if (event && (event.type == "layout-medias-loaded" || event.type == "load-layout-medias-error"))
                    done();
            };

            dispatcher.on("event", handler, this.data);
            timer = setTimeout(done, 10000);   // never holds the creation

            this.data.loadLayoutMedias(medias);
        });
    }

    _prepareCover()
    {
        let cover = this.book.cover;

        if (!cover.photosComponents.length)
            return;

        let backCoverLayout = this.data.layouts.backCoverLayout(this.book.themeid);
        if (backCoverLayout)
            cover.applyLayout(backCoverLayout, this.book.pageWidth, this.book.pageHeight, this.book.sliceWidth, "left");

        let frontCoverLayout = this.data.layouts.frontCoverLayout(this.book.themeid);
        if (frontCoverLayout)
            cover.applyLayout(frontCoverLayout, this.book.pageWidth, this.book.pageHeight, this.book.sliceWidth, "right");

        if (backCoverLayout)
        {
            let centerBG = cover.getBackground("slice");
            if (centerBG)
                centerBG.update({ bgcolor : backCoverLayout.bgcolor });
        }
    }

    /** Front cover first (right side, biggest x). */
    _coverComponents()
    {
        return this.book.cover.photosComponents.sort((a, b) => b.x - a.x);
    }

    /** The photo chosen for the cover : the curated (or Claude's) one, else the best scored. */
    _chosenCoverPhoto(result)
    {
        if (result.cover)
            return result.cover;

        let kept = result.kept || [];

        return kept.length ? kept.reduce((a, b) => (this._score(b) > this._score(a) ? b : a)) : null;
    }

    /** One photo per cover component : the curated cover first, then the best scored ones. */
    _coverPhotos(result)
    {
        let count = this._coverComponents().length;

        if (!count)
            return [];

        let candidates = result.kept.slice().sort((a, b) => this._score(b) - this._score(a));
        if (result.cover)
        {
            candidates = candidates.filter((photo) => photo != result.cover);
            candidates.unshift(result.cover);
        }

        return candidates.slice(0, count);
    }

    _fillCover(photos)
    {
        let components = this._coverComponents();
        let chain = Promise.resolve();

        for (let i = 0; i < components.length && i < photos.length; i++)
        {
            let component = components[i], photo = photos[i];
            chain = chain.then(() => this._assign(component, photo, true));
        }

        return chain;
    }

    // ------------------------------- title ------------------------------- //

    /**
     * With a title, the front cover follows one fixed design : white background, the photo framed by a
     * margin of coverMargin of the width and coverPhotoHeight of the height, the title centered below
     * it and, when the photos carry capture dates, a period subtitle ("Septembre 2026", "Fin 2026",
     * "Année 2026"...). The theme's cover photo and text components are replaced.
     */
    _titleCoverLayout(title, options)
    {
        options = options || {};

        this._coverTitleRect = null;

        if (!(title || "").trim() && !options.force)
            return;

        let s = this.settings;
        let cover = this.book.cover, width = this.book.pageWidth, height = this.book.pageHeight, slice = this.book.sliceWidth || 0;
        let margin = width * s.coverMargin;
        let photoRect = this._coverPhotoRect(this._coverPhotoScale || 1);
        let photoHeight = photoRect.band;

        let photo = new models.ThemeLayoutComponent({
            component_type : "photo",
            x : photoRect.x,
            y : photoRect.y,
            width : photoRect.width,
            height : photoRect.height,
            width_inner : 0,
            height_inner : 0
        });

        // the large shape of the collection, around the photo and under it (see _framedLayout)
        let components = [photo];

        if (this._coverShapeSource)
        {
            let shapeMargin = s.coverShapeMargin;

            components.unshift(new models.ThemeLayoutComponent(Object.assign({}, this._coverShapeSource.JSON ? this._coverShapeSource.JSON() : this._coverShapeSource, {
                component_type : "shape",
                x : photoRect.x - shapeMargin / 2,
                y : photoRect.y - shapeMargin / 2,
                width : photoRect.width + shapeMargin,
                height : photoRect.height + shapeMargin,
                rotation : 0
            })));
        }

        cover.applyLayout(new models.ThemeLayout({ components : components }), width, height, slice, "right");

        // plain white background behind the framed photo, or the one of a collection layout
        // (options.background : a layout component ; null keeps the cover's own)
        let background = cover.getBackground ? cover.getBackground("right") : null;
        if (background && options.background === undefined)
            background.update({ content : "", bgcolor : "#ffffff" });
        else if (background && options.background)
            background.update({ content : options.background.content || "", bgcolor : options.background.bgcolor || "rgba(0,0,0,0)" });

        // the photo of such a cover is smaller : the title and its subtitle come up towards it
        let lift = ((this._coverPhotoScale || 1) < 1) ? s.coverShapeTitleLift : 0;
        let titleTop = margin + photoHeight + height * 0.04 - lift;

        this._coverTitleRect = {
            x : width + slice + margin,
            width : width - 2 * margin,
            titleY : titleTop,
            subtitleY : titleTop + s.coverTitleFontSize * 2
        };
    }

    /**
     * Writes the title on the front cover : the two lines of the album category when its design was
     * applied, else the design of _titleCoverLayout when it ran (with the period subtitle), else the
     * theme's text component (the "title" one, else the biggest) keeping its style, else a new
     * centered text.
     */
    _applyTitle(title)
    {
        title = (title || "").trim();

        // the cover of an album category writes its own lines (covers.DesignedCover), the name of
        // the city of a city break even without a title
        if (this._designedCover)
            return this._designedCover.writeTitle(title);

        if (!title)
            return Promise.resolve();

        let s = this.settings;
        let cover = this.book.cover, width = this.book.pageWidth, height = this.book.pageHeight, slice = this.book.sliceWidth || 0;
        let rect = this._coverTitleRect;

        if (!rect)
        {
            // a kept design (collection cover, theme cover) : the title in its title text, the period
            // in a second text when there is one, their styles kept
            let texts = this._coverTexts();

            if (texts.length)
                return this._applyTitleToTexts(title, texts);

            // a collection design without text is left as it is
            if (!this._coverKeepTexts)
                this._addCoverText(this._coverCase(title), "title", { x : width + slice + width * 0.1, y : height * 0.68, width : width * 0.8 }, s.coverTitleFontSize, s.coverTitleColor);

            return Promise.resolve();
        }

        // white texts on a dark collection background (see _designCover)
        let titleColor = this._coverTextColor || s.coverTitleColor;
        let subtitleColor = this._coverTextColor || s.coverSubtitleColor;

        this._addCoverText(this._coverCase(title), "title", { x : rect.x, y : rect.titleY, width : rect.width }, s.coverTitleFontSize, titleColor);

        let period = this._subtitleFor(title);
        if (period)
            this._addCoverText(this._coverCase(period), "text", { x : rect.x, y : rect.subtitleY, width : rect.width }, s.coverSubtitleFontSize, subtitleColor);

        return Promise.resolve();
    }

    /**
     * The place our own design gives the cover photo : the printable width and coverPhotoHeight of
     * the page, at the given scale, centered in the band it fills at full size so that the texts
     * underneath never move. `band` is that full height, the one the title follows.
     */
    _coverPhotoRect(scale)
    {
        let s = this.settings;
        let width = this.book.pageWidth, height = this.book.pageHeight;
        let margin = width * s.coverMargin;
        // room needed under the photo : gap, title, subtitle (pages wider than tall get a shorter photo)
        let textBlock = height * 0.04 + s.coverTitleFontSize * 2 + s.coverSubtitleFontSize * 2;
        let band = Math.min(height * s.coverPhotoHeight, height - 2 * margin - textBlock);
        let photoWidth = (width - 2 * margin) * scale;

        return {
            x : (width - photoWidth) / 2,
            y : margin + (band - band * scale) / 2,
            width : photoWidth,
            height : band * scale,
            band : band
        };
    }

    /**
     * A cover left by an earlier automatic creation : its photo fills exactly the place our design
     * gives it, at full size or reduced. Nothing of the collection is laid out that way, and the
     * customer never resizes a photo to the pixel, so such a cover is ours to build again.
     */
    _coverIsOurDesign()
    {
        let photo = this._coverComponents()[0];

        if (!photo)
            return false;

        let offset = this.book.pageWidth + (this.book.sliceWidth || 0);
        let close = (value, expected) => Math.abs(Number(value) - expected) < 1;
        let matches = (rect) => close(photo.x, offset + rect.x) && close(photo.y, rect.y)
            && close(photo.width, rect.width) && close(photo.height, rect.height);

        return matches(this._coverPhotoRect(1)) || matches(this._coverPhotoRect(1 - this.settings.coverShapePhotoShrink));
    }

    /**
     * The cover subtitle, unless it says the same as the title : a title picked among the date ones
     * ("Septembre 2026") is often the period itself, and the cover would carry it twice.
     */
    _subtitleFor(title)
    {
        let subtitle = this._subtitle();
        let plain = (text) => String(text || "").trim().toLowerCase().replace(/\s+/g, " ");

        return (plain(subtitle) && plain(subtitle) == plain(title)) ? "" : subtitle;
    }

    /** The cover subtitle : the one given with the options (album category), else the period of the photos. */
    _subtitle()
    {
        if (typeof this._options.subtitle == "string")
            return this._options.subtitle.trim();

        return this._periodLabel(this._coverPeriodPhotos || []);
    }

    /**
     * A text of a kept design must stay on one line : the paragraph is rendered off screen, on one
     * line, with its own fonts (waited for), and while it is wider than its block every font size it
     * carries is reduced in proportion. Answers a Promise of the html.
     */
    _fitOneLine(html, width)
    {
        return this._fitOneLineMeasured(html, width).then((fit) => fit.html);
    }

    /** _fitOneLine, answering { html, ratio } : ratio is how much the font sizes were scaled (1 : untouched). */
    _fitOneLineMeasured(html, width)
    {
        let untouched = { html : html, ratio : 1 };

        if (!html || !(width > 0) || typeof DOMParser == "undefined" || typeof document == "undefined" || !document.body)
            return Promise.resolve(untouched);

        let doc = new DOMParser().parseFromString(html, "text/html");
        let paragraph = doc.body.querySelector("p") || doc.body.firstElementChild;
        let sized = paragraph ? Array.from(paragraph.querySelectorAll("[style*='font-size']")) : [];
        let text = paragraph ? (paragraph.textContent || "").trim() : "";

        if (!paragraph || !sized.length || !text)
            return Promise.resolve(untouched);

        let sizes = sized.map((node) => parseFloat(node.style.fontSize));
        if (sizes.some((size) => !(size > 0)))
            return Promise.resolve(untouched);

        // the fonts of the paragraph, loaded before measuring (a bounded wait : a missing font falls back)
        let families = [];
        sized.forEach((node) => {
            let family = (node.style.fontFamily || "").replace(/["']/g, "").trim();
            if (family && families.indexOf(family) == -1)
                families.push(family);
        });

        let loaded = Promise.resolve();
        if (document.fonts && document.fonts.load && families.length)
        {
            loaded = Promise.race([
                Promise.all(families.map((family) => document.fonts.load("12px \"" + family + "\"").catch(() => null))),
                new Promise((resolve) => setTimeout(resolve, 2000))
            ]);
        }

        return loaded.then(() => {
            let measurer = document.createElement("div");
            measurer.style.cssText = "position:absolute;left:-100000px;top:0;visibility:hidden;display:inline-block;white-space:nowrap;line-height:normal;";
            document.body.appendChild(measurer);

            // the stage keeps a margin inside the text block (stage Text component, MARGINS : 10 in all)
            let available = (width - 10) * 0.98;
            let ratio = 1;

            try
            {
                for (let i = 0; i < 12; i++)
                {
                    measurer.innerHTML = paragraph.outerHTML;
                    let measured = measurer.getBoundingClientRect().width;
                    if (!(measured > available))
                        break;

                    // one step below the exact proportion, never above 2 px
                    let step = Math.max(0.5, Math.min(0.97, available / measured));
                    ratio = ratio * step;

                    let floor = Math.min.apply(null, sizes) * ratio;
                    if (floor < 2)
                        break;

                    sized.forEach((node, index) => { node.style.fontSize = (Math.round(sizes[index] * ratio * 100) / 100) + "px"; });
                }
            }
            catch (e) {}

            measurer.remove();

            return { html : doc.body.innerHTML, ratio : ratio };
        });
    }

    _addCoverText(text, contentType, rect, fontSize, color)
    {
        let s = this.settings;
        let cover = this.book.cover, width = this.book.pageWidth, height = this.book.pageHeight, slice = this.book.sliceWidth || 0;
        let safe = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
        let font = (this.data.fonts && this.data.fonts.find) ? this.data.fonts.find({ name : s.coverFont }) : null;

        cover.addComponent({
            component_type : "text",
            content_type : contentType,
            content : "<p style=\"text-align: center;\"><span style=\"font-family: " + s.coverFont + "; font-size: " + fontSize + "px; color: " + color + ";\">" + safe + "</span></p>",
            text_color : color,
            font_family : font ? font.id : 1,
            font_size : fontSize,
            align : "center",
            x : rect.x,
            y : rect.y,
            width : rect.width,
            height : fontSize * 3,
            height_inner : -1
        }, "right", width, height, slice, true);   // as a layout text : placed as given, its colour kept (chosen for the background here)
    }

    /**
     * The period the capture dates cover, in the site language ("Septembre 2026", "Début 2026",
     * "Été 2026", "Fin 2026", "Année 2026", "2025 - 2026") : the TitleSuggester's rule, the one the
     * cover subtitles of the album categories follow. Empty when no photo carries a capture date.
     */
    _periodLabel(photos)
    {
        return fx.service("TitleSuggester").periodLabel(photos || []);
    }

    /**
     * How a text replacing a cover text must be written : in the style of the text it replaces, its
     * colour and its case. A collection cover is never recoloured — neither its title nor the text
     * taking the subtitle, whatever the colour of the other covers of the collection.
     */
    _replacedTextStyle(component)
    {
        return { color : component ? component.text_color : "" };
    }

    /**
     * Replaces the text of a component's HTML content with the title, keeping the first paragraph's
     * style. `style` tells how the title is written : { color, upper, force }. Without an `upper`
     * the title follows the case of the text it replaces, and the colour is only used when the run
     * carries none of its own, unless `force` says it comes from the collection.
     */
    _replaceText(html, title, options)
    {
        options = options || {};

        let color = options.color || "";
        let style = color ? " style=\"color: " + color + ";\"" : "";
        let plain = (text) => "<p style=\"text-align: center;\"><span" + style + ">" + text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;") + "</span></p>";

        if (!html || typeof DOMParser == "undefined")
            return plain(title);

        let doc = new DOMParser().parseFromString(html, "text/html");
        let paragraphs = doc.body.querySelectorAll("p");

        if (!paragraphs.length)
            return plain(title);

        for (let i = 1; i < paragraphs.length; i++)
            paragraphs[i].remove();

        let first = paragraphs[0];
        first.querySelectorAll(".ql-cursor").forEach((node) => node.remove());

        // a cover written in capitals keeps its title in capitals
        if ((options.upper === undefined) ? this._allCaps(doc.body.textContent) : options.upper)
            title = title.toUpperCase();

        // the first styled run (span, strong, em...) keeps its font, size and colour for the title
        let keep = first.querySelector("[style*='font-size']") || first.querySelector("[style]") || first.querySelector("span, strong, em, b, i, u");

        if (keep)
        {
            keep.textContent = title;

            // the colour of the collection, or the one of the component when the run carries none
            if (color && (options.force || !keep.style.color))
                keep.style.color = color;

            Array.from(first.childNodes).forEach((node) => { if (node != keep && !node.contains(keep)) node.remove(); });
        }
        else
        if (color)
            first.innerHTML = plain(title).replace(/^<p[^>]*>/, "").replace(/<\/p>$/, "");
        else
            first.textContent = title;

        return doc.body.innerHTML;
    }

    /** The title as the collection writes its own cover text : in capitals when that text is. */
    _coverCase(text)
    {
        return this._coverTextUpper ? String(text || "").toUpperCase() : text;
    }

    /** A text written in capitals : it has letters, and none of them is lower case. */
    _allCaps(text)
    {
        let value = String(text || "").trim();

        return !!value && value != value.toLowerCase() && value == value.toUpperCase();
    }

    // ------------------------------- crops ------------------------------- //

    /**
     * Puts a photo in a component. The crop goes through smartcrop when the component and the
     * photo do not share their ratio (or when asked) ; otherwise the stage fills the component the
     * usual way, centering on the faces the curator found.
     */
    _assign(component, photo, smart)
    {
        let plain = () => component.update({ content : photo.id, width_inner : 0 });

        if (!photo.width || !photo.height || !component.width || !component.height)
        {
            plain();
            return Promise.resolve();
        }

        let ratio = component.width / component.height;
        let photoRatio = photo.width / photo.height;
        let rotated = photo.orientation && Math.abs(Number(photo.orientation)) % 180 == 90;
        let needed = smart || Math.abs(ratio - photoRatio) / photoRatio > 0.03;

        if (!needed || rotated)
        {
            plain();
            return Promise.resolve();
        }

        return this.curator.cropFor(photo, ratio).then((crop) => {
            if (!crop)
                plain();
            else
                this._applyCrop(component, photo, crop);
        });
    }

    _applyCrop(component, photo, crop)
    {
        let width = component.width, height = component.height;

        let widthInner = width / crop.width;
        let heightInner = widthInner * photo.height / photo.width;

        if (heightInner * crop.height < height)
        {
            heightInner = height / crop.height;
            widthInner = heightInner * photo.width / photo.height;
        }

        let x = -crop.x * widthInner, y = -crop.y * heightInner;
        x = Math.min(0, Math.max(width - widthInner, x));
        y = Math.min(0, Math.max(height - heightInner, y));

        // the "smart-crop" tag tells the stage not to refill the component when the image loads
        component.update("smart-crop", {
            content : photo.id,
            width_inner : widthInner,
            height_inner : heightInner,
            x_inner : x,
            y_inner : y,
            zoom : 1
        });
    }

    _score(photo)
    {
        return (photo.curation && photo.curation.score !== undefined) ? photo.curation.score : 0.5;
    }

    _maxGroups(count)
    {
        let current = this._sides().length;
        let max = this.book.maxPages ? Math.max(this.book.maxPages, current) : current;
        let pages = Math.min(max, Math.max(current, Math.ceil(count / this.settings.photosPerPage)));

        // one spread per moment on average
        return Math.max(1, Math.min(count, Math.floor(pages / 2)));
    }

    // ------------------------------ fallback ----------------------------- //

    /** Previous behaviour : chronological order, one photo per page, cover photos taken from the start. */
    _placeSimple(photos)
    {
        photos = curator.MomentClustering.sort(photos);

        this._coverPeriodPhotos = photos;

        return this._chooseCoverPhoto(photos)
            .then((cover) => {
                // the cover is filled from the head of the list (_placeSimplePagesAfterTitle)
                if (cover && photos.indexOf(cover) > 0)
                {
                    photos.splice(photos.indexOf(cover), 1);
                    photos.unshift(cover);
                }

                return this._designCover(this._options.title);
            })
            .then(() => this._placeSimplePages(photos));
    }

    _placeSimplePages(photos)
    {
        return this._applyTitle(this._options.title).then(() => this._placeSimplePagesAfterTitle(photos));
    }

    _placeSimplePagesAfterTitle(photos)
    {
        let coverPhotos = this.book.cover.photosComponents.sort((a, b) => b.x - a.x);
        for (let i = 0; i < coverPhotos.length; i++)
        {
            let photo = photos.shift();
            if (photo)
                coverPhotos[i].update({ content : photo.id, width_inner : 0 });
        }

        let pages = this.book.innerPages;
        for (let i = 0; i < pages.length; i++)
            pages[i].removeAllContent();

        this._ensurePages(photos.length);

        let sides = this._sides();
        for (let i = 0; i < photos.length && i < sides.length; i++)
        {
            let photo = photos[i], slot = sides[i];
            let layout = this.generator.getLayouts([photo])[0];

            slot.page.applyLayout(layout, this.book.pageWidth, this.book.pageHeight, 0, slot.side);

            let component = this._photoComponents(slot.page, slot.side)[0];
            if (component)
                component.update({ content : photo.id, width_inner : 0 });
        }
    }
});
